import { Component, inject, input, signal, effect, ElementRef, ViewChild, AfterViewChecked, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { AvatarPersonComponent } from 'components/avatar/avatar-person/avatar-person.component';
import { LoadingComponent } from 'components/loading/loading.component';
import { EventType } from '@weavix/models/src/badges/event';
import { MapWalt } from 'weavix-shared/models/weavix-map.model';
import { WaltSession } from '@weavix/models/src/walt/walt';
import { AlertService, ServiceError } from 'weavix-shared/services/alert.service';
import { WaltService } from 'weavix-shared/services/walt.service';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { poweredOffReasonKey } from '../powered-off-reason';

const PAGE_SIZE = 10;

@AutoUnsubscribe()
@Component({
    selector: 'app-walt-detail-history',
    templateUrl: './walt-detail-history.component.html',
    styleUrls: [
        './walt-detail-history.component.scss',
        '../walt-detail.component.scss',
    ],
    standalone: true,
    host: { 'data-testid': 'WaltDetailHistory' },
    imports: [
        CommonModule,
        TranslateModule,
        AvatarPersonComponent,
        LoadingComponent,
    ],
})
export class WaltDetailHistoryComponent implements AfterViewChecked, OnDestroy {
    private readonly alertService = inject(AlertService);
    private readonly waltService = inject(WaltService);

    readonly walt = input.required<MapWalt>();

    readonly loading = signal(false);
    readonly loadingMore = signal(false);
    readonly sessions = signal<WaltSession[]>([]);
    readonly hasMore = signal(false);

    readonly EventType = EventType;
    readonly poweredOffReasonKey = poweredOffReasonKey;

    @ViewChild('sentinel') private readonly sentinelRef?: ElementRef<HTMLDivElement>;

    private nextCursor: string | undefined;
    private lastLoadedWaltId: string | null = null;
    private observer: IntersectionObserver | null = null;
    private observedSentinel: Element | null = null;

    /** Whether a session is Currently Active: the newest session, still open, on a walt that is logged in. */
    isSessionActive(session: WaltSession, index: number): boolean {
        return index === 0 && !session.endType && !!this.walt().loggedIn;
    }

    constructor() {
        effect(() => {
            const waltId = this.walt().id;
            if (this.lastLoadedWaltId === waltId) return;
            this.lastLoadedWaltId = waltId;
            this.nextCursor = undefined;
            void this.loadPage(waltId, undefined, true);
        }, { allowSignalWrites: true });
    }

    ngAfterViewChecked(): void {
        const sentinelEl = this.sentinelRef?.nativeElement ?? null;
        if (sentinelEl === this.observedSentinel) return;

        // Sentinel element changed (appeared or disappeared) — re-wire the observer.
        this.disconnectObserver();
        this.observedSentinel = sentinelEl;

        if (sentinelEl) {
            this.observer = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && this.hasMore() && !this.loadingMore()) {
                    void this.loadMore();
                }
            }, { threshold: 0.1 });
            this.observer.observe(sentinelEl);
        }
    }

    ngOnDestroy(): void {
        this.disconnectObserver();
    }

    private disconnectObserver(): void {
        this.observer?.disconnect();
        this.observer = null;
    }

    private async loadMore(): Promise<void> {
        await this.loadPage(this.walt().id, this.nextCursor, false);
    }

    private async loadPage(waltId: string, before: string | undefined, reset: boolean): Promise<void> {
        if (reset) {
            this.loading.set(true);
        } else {
            this.loadingMore.set(true);
        }

        try {
            const result = await this.waltService.getWaltSessions(this, waltId, before, PAGE_SIZE);
            // Discard the response if the user switched to a different walt while this request was in flight.
            if (waltId !== this.walt().id) return;
            const incoming = (result?.sessions ?? []) as WaltSession[];
            this.nextCursor = result?.nextCursor;
            this.sessions.update(prev => reset ? incoming : [...prev, ...incoming]);
            this.hasMore.set(!!result?.nextCursor);
        } catch (e: any) {
            this.alertService.sendServiceError(e, ServiceError.Get, 'walt.walt');
        } finally {
            this.loading.set(false);
            this.loadingMore.set(false);
        }
    }
}
